import React, { useRef } from "react";
import { Platform, View } from "react-native";
import YouTube from "react-youtube";
import WebView from "react-native-webview";
import { config } from "../config";
import { useTheme } from "../providers/ThemeProvider";
import { useStalledCheck } from "../hooks/useStalledCheck";

type YoutubeVideoProps = {
  videoId: string;
  loadNextItem: () => void;
  loop: boolean;
  onPlaybackFailed: () => void;
};

export const YoutubeVideo = (props: YoutubeVideoProps) => {
  const theme = useTheme();

  if (Platform.OS === "android") {
    return (
      <WebView
        source={{
          uri: `${config.tvHostUrl}/yt-embed/${props.videoId}?loop=${props.loop ? 1 : 0}&scale=${config.pixelDensity}`,
        }}
        style={{ backgroundColor: theme.colors.black }}
        mediaPlaybackRequiresUserAction={false}
        hasTVPreferredFocus
        onMessage={(ev) => {
          if (ev.nativeEvent.data === "onVideoEnd") {
            props.loadNextItem();
          } else if (ev.nativeEvent.data === "onPlaybackFailed") {
            props.onPlaybackFailed();
          }
        }}
      />
    );
  }

  return <YoutubeEmbed {...props} />;
};

export const YoutubeEmbed = ({
  videoId,
  loadNextItem,
  loop,
  onPlaybackFailed,
}: YoutubeVideoProps) => {
  const youtubeRef = useRef<YouTube>(null);
  const theme = useTheme();

  useStalledCheck(
    () =>
      youtubeRef.current?.internalPlayer?.getCurrentTime() ??
      Promise.resolve(0),
    loadNextItem,
  );

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.black }}>
      <YouTube
        ref={youtubeRef}
        style={{ position: "absolute", width: "100%", height: "100%" }}
        videoId={videoId}
        opts={{
          playerVars: {
            autoplay: 1,
            enablejsapi: 1,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            fs: 0,
            iv_load_policy: 3,
          },
          width: "100%",
          height: "100%",
        }}
        onError={(err) => {
          // copyright error
          if (err.data.toString() === "150") {
            onPlaybackFailed();
          }
        }}
        onStateChange={(ev) => {
          if (ev.data !== 0) return;

          loadNextItem();
          if (loop) {
            youtubeRef.current?.internalPlayer?.playVideo();
          }
        }}
      />
    </View>
  );
};
